import axiosInstance from 'api/axiosInstance/axiosInstance'
import type { SoldVehicle, SoldVehicleSearchCriteria } from '../types'

const computeRatio = (tfc?: number | null, spot?: number | null) => {
  if (typeof tfc !== 'number' || typeof spot !== 'number' || spot === 0) return null
  return tfc / spot
}

const getNumber = (value?: unknown) =>
  typeof value === 'number' && Number.isFinite(value) ? value : null

const pickVehicleId = (raw: Record<string, unknown>) => {
  const candidate = [raw.id, raw.versionId, raw.vin, raw.licensePlate, raw.erpId].find(
    (value): value is string => typeof value === 'string' && value.length > 0,
  )
  return candidate ?? null
}

const toSoldVehicle = (raw: Record<string, unknown>): SoldVehicle | null => {
  const {
    priceIncVatIncFeesExcAdminFees: priceValue,
    priceIncVatExcFeesExcAdminFees: tfcValue,
    legacyPriceSpot: spotValue,
  } = raw as {
    priceIncVatIncFeesExcAdminFees?: unknown
    priceIncVatExcFeesExcAdminFees?: unknown
    legacyPriceSpot?: unknown
  }
  const price = getNumber(priceValue)
  const tfc = getNumber(tfcValue)
  const spot = getNumber(spotValue)

  const id = pickVehicleId(raw)
  if (!id) return null
  if (price === null && tfc === null) return null

  return {
    id,
    erpId: typeof raw.erpId === 'string' ? raw.erpId : null,
    make: (raw.make as string) ?? null,
    model: (raw.model as string) ?? null,
    version: (raw.version as string) ?? null,
    normalVersion: (raw.normalVersion as string) ?? null,
    type: (raw.type as string) ?? null,
    dateSold: (raw.dateSold as string) ?? null,
    dateFirstReg: typeof raw.dateFirstReg === 'number' ? raw.dateFirstReg : null,
    mileage: typeof raw.mileage === 'number' ? raw.mileage : null,
    age: typeof raw.age === 'number' ? raw.age : null,
    color: (raw.color as string) ?? null,
    fuelType: (raw.fuelType as string) ?? null,
    noteExterior: typeof raw.noteExterior === 'number' ? raw.noteExterior : null,
    noteInterior: typeof raw.noteInterior === 'number' ? raw.noteInterior : null,
    noteExpert: typeof raw.noteExpert === 'number' ? raw.noteExpert : null,
    priceIncVatExcFeesExcAdminFees: tfc,
    legacyPriceSpot: spot,
    priceIncVatIncFeesExcAdminFees: price,
    legacyPriceSpotTfcRatio: computeRatio(tfc, spot),
    reconditioningCosts:
      typeof raw.reconditioningCosts === 'number' ? raw.reconditioningCosts : null,
    auctionRoomName: (raw.auctionRoomName as string) ?? null,
    auctionCountryCode: (raw.auctionCountryCode as string) ?? null,
    sellerComment: (raw.sellerComment as string) ?? null,
    nonOp: typeof raw.nonOp === 'boolean' ? raw.nonOp : null,
  }
}

const extractArray = (data: unknown): Array<Record<string, unknown>> => {
  if (Array.isArray(data)) return data as Array<Record<string, unknown>>
  if (data && typeof data === 'object') {
    const { payload, items } = data as { payload?: unknown; items?: unknown; success?: unknown }
    if (Array.isArray(payload)) return payload as Array<Record<string, unknown>>
    if (Array.isArray(items)) return items as Array<Record<string, unknown>>
  }
  return []
}

export const fetchSoldVehicles = async (
  criteria: SoldVehicleSearchCriteria,
): Promise<Array<SoldVehicle>> => {
  const response = await axiosInstance.post('/histoRead', criteria)
  const json = response.data
  const list = extractArray(json)
  const parsed = list
    .map((item) => toSoldVehicle(item))
    .filter((item): item is SoldVehicle => Boolean(item))
    .filter((item) => {
      const price = item.priceIncVatExcFeesExcAdminFees ?? item.priceIncVatIncFeesExcAdminFees
      return typeof price === 'number' && price > 0
    })
  return parsed
}

export const fetchVehicleBatch = async (
  criteria: SoldVehicleSearchCriteria,
): Promise<Array<SoldVehicle>> => {
  try {
    const vehicles = await fetchSoldVehicles(criteria)
    if (vehicles.length > 0) {
      return vehicles
    }
  } catch (error) {
    if (process.env.NODE_ENV !== 'production') {
      // eslint-disable-next-line no-console
      console.debug('Unable to fetch sold vehicles for mini-jeu', error)
    }
  }
  throw new Error('fetchVehicleBatch: empty response')
}
